import React, { useMemo } from 'react';
import './App.css';
import { Layout, ConfigProvider, theme, ColorPicker, Dropdown, Tooltip, Modal, App as AntdApp } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import Viewport3D, { HERO_POSITIONS } from './components/Viewport3D';
import UVPanel from './components/UVPanel';
import LayersPanel from './components/LayersPanel';
import Timeline from './components/Timeline';
import HotkeyModal from './components/HotkeyModal';
import StampPickerModal from './components/StampPickerModal';
import AboutModal from './components/AboutModal';
import UserGuideModal from './components/UserGuideModal';
import WhatsNewModal from './components/WhatsNewModal';
import { readNewestAutosave, clearAutosave, AutosaveSlot } from './utils/autosaveStorage';
import { useAppStore } from './store/useAppStore';
import { viewportActions } from './viewportActions';
import { TEX_RESOLUTIONS, BRUSH_MIN, BRUSH_MAX, TEXT_FONTS } from './paintingConstants';
import { C } from './theme';
import { toolBtn, actionBtn, sep, rowStyle } from './components/toolbar/toolbarStyles';
import FillContextStrip from './components/toolbar/FillContextStrip';
import DrawEraseContextStrip from './components/toolbar/DrawEraseContextStrip';
import BlurContextStrip from './components/toolbar/BlurContextStrip';
import StampTextContextStrip from './components/toolbar/StampTextContextStrip';
import HotkeyHud from './components/HotkeyHud';
import pkg from '../package.json';

const { Content, Footer } = Layout;

// Platform-aware modifier key labels
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
const CTRL = isMac ? 'Cmd' : 'Ctrl';
const OPT  = isMac ? 'Option' : 'Alt';

// ── Shared toolbar button style helpers are in components/toolbar/toolbarStyles.ts ──

/** Menu checkmark indicator — module-level so it is never recreated per render. */
const chk = (active: boolean) => (
  <span style={{ display: 'inline-block', width: 16, textAlign: 'center', fontSize: 11 }}>
    {active ? '\u2713' : ''}
  </span>
);

/** Menu label with optional right-aligned hotkey hint. */
const menuLabel = (name: React.ReactNode, hotkey?: string) => (
  <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 40, minWidth: 160 }}>
    <span>{name}</span>
    {hotkey && <span style={{ color: C.textDim, fontSize: 11, fontFamily: 'monospace', flexShrink: 0 }}>{hotkey}</span>}
  </span>
);

function App() {
  const rightWidth = 400;

  // ── Store reads ──────────────────────────────────────────────────────────
  const isDrawingActive = useAppStore((s) => s.isDrawingActive);
  const toolMode = useAppStore((s) => s.toolMode);
  const setToolMode = useAppStore((s) => s.setToolMode);
  const brushSize = useAppStore((s) => s.brushSize);
  const setBrushSize = useAppStore((s) => s.setBrushSize);
  const brushOpacity = useAppStore((s) => s.brushOpacity);
  const setBrushOpacity = useAppStore((s) => s.setBrushOpacity);
  const blurRadius = useAppStore((s) => s.blurRadius);
  const setBlurRadius = useAppStore((s) => s.setBlurRadius);
  const pickerValue = useAppStore((s) => s.pickerValue);
  const setPickerValue = useAppStore((s) => s.setPickerValue);
  const setBrushColor = useAppStore((s) => s.setBrushColor);
  const usePressure = useAppStore((s) => s.usePressure);
  const setUsePressure = useAppStore((s) => s.setUsePressure);
  const simPressure = useAppStore((s) => s.simPressure);
  const setSimPressure = useAppStore((s) => s.setSimPressure);
  const simPressureRandomness = useAppStore((s) => s.simPressureRandomness);
  const setSimPressureRandomness = useAppStore((s) => s.setSimPressureRandomness);
  const drawProjectionMode = useAppStore((s) => s.drawProjectionMode);
  const setDrawProjectionMode = useAppStore((s) => s.setDrawProjectionMode);
  const stampProjectionMode = useAppStore((s) => s.stampProjectionMode);
  const setStampProjectionMode = useAppStore((s) => s.setStampProjectionMode);
  const stampFlipX = useAppStore((s) => s.stampFlipX);
  const setStampFlipX = useAppStore((s) => s.setStampFlipX);
  const stampFlipY = useAppStore((s) => s.stampFlipY);
  const setStampFlipY = useAppStore((s) => s.setStampFlipY);
  const inverseWarpK = useAppStore((s) => s.inverseWarpK);
  const setInverseWarpK = useAppStore((s) => s.setInverseWarpK);
  const inverseWarpStrength = useAppStore((s) => s.inverseWarpStrength);
  const setInverseWarpStrength = useAppStore((s) => s.setInverseWarpStrength);
  const stampSize = useAppStore((s) => s.stampSize);
  const setStampSize = useAppStore((s) => s.setStampSize);
  const stampRotation = useAppStore((s) => s.stampRotation);
  const setStampRotation = useAppStore((s) => s.setStampRotation);
  const smoothness = useAppStore((s) => s.smoothness);
  const setSmoothness = useAppStore((s) => s.setSmoothness);
  const texRes = useAppStore((s) => s.texRes);
  const setTexRes = useAppStore((s) => s.setTexRes);
  const dupMode = useAppStore((s) => s.dupMode);
  const setDupMode = useAppStore((s) => s.setDupMode);
  const viewportBgMode = useAppStore((s) => s.viewportBgMode);
  const setViewportBgMode = useAppStore((s) => s.setViewportBgMode);
  const heroFeaturesEnabled = useAppStore((s) => s.heroFeaturesEnabled);
  const setHeroFeaturesEnabled = useAppStore((s) => s.setHeroFeaturesEnabled);
  const showGrid = useAppStore((s) => s.showGrid);
  const setShowGrid = useAppStore((s) => s.setShowGrid);
  const showManipulator = useAppStore((s) => s.showManipulator);
  const setShowManipulator = useAppStore((s) => s.setShowManipulator);
  const showFps = useAppStore((s) => s.showFps);
  const setShowFps = useAppStore((s) => s.setShowFps);
  const showHotkeyHud = useAppStore((s) => s.showHotkeyHud);
  const setShowHotkeyHud = useAppStore((s) => s.setShowHotkeyHud);
  const overlayArchitecture = useAppStore((s) => s.overlayArchitecture);
  const setOverlayArchitecture = useAppStore((s) => s.setOverlayArchitecture);
  const overlaySafety = useAppStore((s) => s.overlaySafety);
  const setOverlaySafety = useAppStore((s) => s.setOverlaySafety);
  const stampImage = useAppStore((s) => s.stampImage);
  const stampPickerOpen = useAppStore((s) => s.stampPickerOpen);
  const setStampPickerOpen = useAppStore((s) => s.setStampPickerOpen);
  const setProjectModalOpen = useAppStore((s) => s.setProjectModalOpen);
  const setRenderModalOpen = useAppStore((s) => s.setRenderModalOpen);
  const hotkeysOpen = useAppStore((s) => s.hotkeysOpen);
  const setHotkeysOpen = useAppStore((s) => s.setHotkeysOpen);
  const [aboutOpen, setAboutOpen] = React.useState(false);
  const [guideOpen, setGuideOpen] = React.useState(false);
  const lastSeenVersion = useAppStore((s) => s.lastSeenVersion);
  const setLastSeenVersion = useAppStore((s) => s.setLastSeenVersion);
  const [whatsNewOpen, setWhatsNewOpen] = React.useState(false);
  // Captured on mount so the modal can compare against the version the user
  // previously had, even after setLastSeenVersion updates the store.
  const [whatsNewSince, setWhatsNewSince] = React.useState('');
  const [pendingResTarget, setPendingResTarget] = React.useState<typeof TEX_RESOLUTIONS[number] | null>(null);
  const currentProjectName = useAppStore((s) => s.currentProjectName);
  // ── Autosave status & recovery ──────────────────────────────────────────
  const autosaveStatus = useAppStore((s) => s.autosaveStatus);
  const [recoverySlot, setRecoverySlot] = React.useState<AutosaveSlot | null>(null);
  // Tracks a 'new' action intercepted by the recovery check.
  const [pendingAction, setPendingAction] = React.useState<'new' | null>(null);
  // Tracks a picked file that is waiting on the user's recovery decision.
  const [pendingFileData, setPendingFileData] = React.useState<{ file: File; handle: any | null } | null>(null);

  // Show What's New popup when the app version is newer than last seen.
  React.useEffect(() => {
    const currentVersion: string = pkg.version;
    if (currentVersion !== lastSeenVersion) {
      setWhatsNewSince(lastSeenVersion);
      setLastSeenVersion(currentVersion);
      setWhatsNewOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    readNewestAutosave().then((slot) => {
      if (slot) setRecoverySlot(slot);
    }).catch(() => {});
  }, []);

  // Set each render so the closure always captures current state setters.
  // Viewport3D calls this after the user picks a file (Open dialog / ProjectModal).
  viewportActions.loadFileWithAutosaveCheck.current = async (file: File, handle?: any) => {
    const derivedName = file.name.replace(/\.balls$/, '');
    const slot = await readNewestAutosave().catch(() => null);
    if (slot && slot.name === derivedName) {
      // Autosave matches the project being loaded — offer recovery.
      setPendingFileData({ file, handle: handle ?? null });
      setRecoverySlot(slot);
    } else {
      // No matching autosave — load directly (clears autosave inside loadFile).
      await viewportActions.loadFile.current(file, handle);
    }
  };

  // Called by 'New Project' menu item: check for any autosave first.
  const checkAutosaveBeforeAction = React.useCallback(async () => {
    const slot = await readNewestAutosave().catch(() => null);
    if (slot) {
      setPendingAction('new');
      setRecoverySlot(slot);
    } else {
      viewportActions.newProject.current();
    }
  }, []);

  const handleRestore = React.useCallback((json: string) => {
    setRecoverySlot(null);
    setPendingAction(null);
    setPendingFileData(null);
    clearAutosave().catch(() => {});
    // Re-use the existing loadProject path by wrapping JSON in a File.
    const file = new File([json], 'autosave.balls', { type: 'application/json' });
    viewportActions.loadFile.current(file);
  }, []);

  const handleDiscard = React.useCallback(() => {
    const action = pendingAction;
    const fileData = pendingFileData;
    setRecoverySlot(null);
    setPendingAction(null);
    setPendingFileData(null);
    clearAutosave().catch(() => {});
    if (action === 'new') {
      viewportActions.newProject.current();
    } else if (fileData) {
      // User discarded the autosave — load the original file from disk.
      viewportActions.loadFile.current(fileData.file, fileData.handle ?? undefined);
    }
  }, [pendingAction, pendingFileData]);
  // ── Tablet mode ─────────────────────────────────────────────────────────
  const tabletMode = useAppStore((s) => s.tabletMode);
  const setTabletMode = useAppStore((s) => s.setTabletMode);
  const rightPanelOpen = useAppStore((s) => s.rightPanelOpen);
  const setRightPanelOpen = useAppStore((s) => s.setRightPanelOpen);
  const timelineOpen = useAppStore((s) => s.timelineOpen);
  const setTimelineOpen = useAppStore((s) => s.setTimelineOpen);
  const uvViewportMain    = useAppStore((s) => s.uvViewportMain);
  const setUvViewportMain = useAppStore((s) => s.setUvViewportMain);
  const clipboardPasteCanvas  = useAppStore((s) => s.clipboardPasteCanvas);
  const clipboardPasteScale   = useAppStore((s) => s.clipboardPasteScale);
  const setClipboardPasteScale = useAppStore((s) => s.setClipboardPasteScale);
  const pastePreviewUrl = React.useMemo(
    () => clipboardPasteCanvas?.toDataURL('image/webp', 0.7) ?? null,
    [clipboardPasteCanvas],
  );

  // Ctrl/Cmd+V — context-aware paste (skip if a text input is focused).
  // • Stamp picker open      → skip; the modal's own paste listener handles it.
  // • Stamp tool active      → paste directly as stamp image (no modal needed).
  // • All other tools        → paste image as full layer (scale + commit flow).
  React.useEffect(() => {
    const handler = async (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== 'v') return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement | null)?.isContentEditable) return;
      const st = useAppStore.getState();
      // When the stamp picker modal is open let its own `paste` event handler run.
      if (st.stampPickerOpen) return;
      e.preventDefault();
      // Stamp tool active → paste clipboard image directly as the stamp image.
      if (st.toolMode === 'stamp') {
        try {
          const items = await navigator.clipboard.read();
          for (const item of items) {
            const imageType = item.types.find((t: string) => t.startsWith('image/'));
            if (!imageType) continue;
            const blob = await item.getType(imageType);
            const url = URL.createObjectURL(blob);
            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
              const el = new Image();
              el.onload = () => resolve(el);
              el.onerror = reject;
              el.src = url;
            });
            URL.revokeObjectURL(url);
            const MAX_STAMP = 512;
            const scale = Math.min(1, MAX_STAMP / Math.max(img.naturalWidth, img.naturalHeight));
            const sw = Math.round(img.naturalWidth * scale);
            const sh = Math.round(img.naturalHeight * scale);
            const c = document.createElement('canvas');
            c.width = sw; c.height = sh;
            c.getContext('2d')!.drawImage(img, 0, 0, sw, sh);
            st.setStampImage(c);
            return;
          }
        } catch { /* clipboard access denied or no image — silently ignore */ }
        return;
      }
      // Default: paste image as a full layer (scale + commit flow).
      viewportActions.pasteClipboard.current();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // When UV becomes the main view, project mode is meaningless (no camera) — reset to wrap.
  React.useEffect(() => {
    if (uvViewportMain) {
      if (drawProjectionMode !== 'wrap') setDrawProjectionMode('wrap');
      if (stampProjectionMode !== 'wrap') setStampProjectionMode('wrap');
    }
  }, [uvViewportMain]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Timeline resize ──────────────────────────────────────────────────────
  const [timelineHeight, setTimelineHeight] = React.useState(220);
  const timelineDragRef = React.useRef<{ startY: number; startH: number } | null>(null);
  const onTimelineResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    timelineDragRef.current = { startY: e.clientY, startH: timelineHeight };
    const onMove = (ev: MouseEvent) => {
      if (!timelineDragRef.current) return;
      const delta = timelineDragRef.current.startY - ev.clientY;
      setTimelineHeight(Math.max(80, Math.min(700, timelineDragRef.current.startH + delta)));
    };
    const onUp = () => {
      timelineDragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const textFont = useAppStore((s) => s.textFont);
  const setTextFont = useAppStore((s) => s.setTextFont);
  const textBold = useAppStore((s) => s.textBold);
  const setTextBold = useAppStore((s) => s.setTextBold);
  const textItalic = useAppStore((s) => s.textItalic);
  const setTextItalic = useAppStore((s) => s.setTextItalic);

  const textContent = useAppStore((s) => s.textContent);
  const setTextContent = useAppStore((s) => s.setTextContent);
  const textJustify = useAppStore((s) => s.textJustify);
  const setTextJustify = useAppStore((s) => s.setTextJustify);
  const textLetterSpacing = useAppStore((s) => s.textLetterSpacing);
  const setTextLetterSpacing = useAppStore((s) => s.setTextLetterSpacing);
  const textLineHeight = useAppStore((s) => s.textLineHeight);
  const setTextLineHeight = useAppStore((s) => s.setTextLineHeight);
  const customColors   = useAppStore((s) => s.customColors);
  const addCustomColor = useAppStore((s) => s.addCustomColor);

  // ── Gradient stop tracking ───────────────────────────────────────────────
  // Returns the index of the currently-selected gradient stop by reading the
  // AntD DOM directly. The active handle always has 'ant-color-picker-slider-handle-active'.
  const getActiveGradientStopIdx = (): number => {
    const activeHandle = document.querySelector('.ant-color-picker-slider-handle-active');
    if (!activeHandle) return 0;
    const parent = activeHandle.parentElement;
    if (!parent) return 0;
    const allHandles = Array.from(parent.querySelectorAll('.ant-color-picker-slider-handle'));
    const idx = allHandles.indexOf(activeHandle as HTMLElement);
    return idx >= 0 ? idx : 0;
  };

  // ── Color picker handler ─────────────────────────────────────────────────
  const handleBrushColorChange = (color: any, info: any) => {
    setPickerValue(color);
    const cssString = typeof info === 'string' ? info : (typeof color === 'string' ? color : '');
    const isGradient = cssString.startsWith('linear-gradient') || cssString.startsWith('radial-gradient');

    if (isGradient) {
      if (color && typeof color.getColors === 'function') {
        const stops: Array<{ color: any; percent: number }> = color.getColors();
        // Keep brushColor updated to the first stop so draw/text tools have a valid solid color.
        if (stops.length > 0 && stops[0].color && typeof stops[0].color.toHexString === 'function') {
          const hex8 = stops[0].color.toHexString();
          setBrushColor(hex8.length >= 9 ? hex8.slice(0, 7) : hex8);
        }
      }
      return;
    }

    if (typeof color === 'string') {
      if (color.startsWith('linear-gradient') || color.startsWith('radial-gradient')) return;
      setBrushColor(color);
      return;
    }
    if (color && typeof color.toHexString === 'function') {
      const hex8 = color.toHexString();
      setBrushColor(hex8.length >= 9 ? hex8.slice(0, 7) : hex8);
    }
  };
  const addCurrentColor = () => {
    const c = pickerValue;
    if (!c || typeof c !== 'object') return;
    let hex: string;
    if (typeof (c as any).isGradient === 'function' && (c as any).isGradient()) {
      // Gradient mode: save the currently-selected stop's color.
      const stops: Array<{ color: any; percent: number }> = (c as any).getColors();
      const idx = Math.min(getActiveGradientStopIdx(), stops.length - 1);
      const stopColor = stops[idx]?.color;
      if (!stopColor || typeof stopColor.toHexString !== 'function') return;
      const hex8 = stopColor.toHexString();
      hex = hex8.length >= 9 ? hex8.slice(0, 7) : hex8;
    } else {
      if (typeof (c as any).toHexString !== 'function') return;
      const hex8 = (c as any).toHexString();
      hex = hex8.length >= 9 ? hex8.slice(0, 7) : hex8;
    }
    addCustomColor(hex);
  };

  const eyeDrop = async () => {
    if (!('EyeDropper' in window)) return;
    try {
      const { sRGBHex } = await new (window as any).EyeDropper().open();
      setBrushColor(sRGBHex);
      setPickerValue(sRGBHex);
    } catch { /* user cancelled */ }
  };

  const colorPanelRender = (_: React.ReactNode, { components: { Picker: PickerPanel } }: any) => {
    return (
    <div>
      <PickerPanel />
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 8px 2px',
        borderTop: `1px solid ${C.borderSubtle}`,
        marginTop: 4,
      }}>
        <span style={{ fontSize: 11, color: C.textMuted }}>Swatches</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {'EyeDropper' in window && (
            <Tooltip title="Pick color from screen" placement="left">
              <button
                onClick={eyeDrop}
                style={{ ...toolBtn(false), padding: '1px 5px', fontSize: 13, lineHeight: 1 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block' }}>
                  <path d="M20.71 5.63l-2.34-2.34a1 1 0 0 0-1.41 0l-3.12 3.12-1.41-1.42-1.42 1.42 1.41 1.41-6.6 6.6A2 2 0 0 0 5 16v3h3a2 2 0 0 0 1.42-.59l6.6-6.6 1.41 1.42 1.42-1.42-1.42-1.41 3.12-3.12a1 1 0 0 0 0-1.65z"/>
                </svg>
              </button>
            </Tooltip>
          )}
          <Tooltip title="Save current color" placement="left">
            <button
              onClick={addCurrentColor}
              style={{ ...toolBtn(false), padding: '1px 6px', fontSize: 13, lineHeight: 1 }}
            >+</button>
          </Tooltip>
        </div>
      </div>
      {customColors.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 8px 8px' }}>
          {customColors.map((color) => (
            <div
              key={color}
              title={color}
              style={{
                width: 20, height: 20, background: color,
                borderRadius: 3, cursor: 'pointer',
                border: `1px solid ${C.borderFaint}`,
                flexShrink: 0,
              }}
              onClick={() => {
                const pv = pickerValue;
                if (pv && typeof (pv as any).getColors === 'function') {
                  // Gradient mode: query the DOM right now to find which stop is selected.
                  const stops: Array<{ color: any; percent: number }> = (pv as any).getColors();
                  const activeIdx = getActiveGradientStopIdx();
                  const safeIdx = Math.min(activeIdx, stops.length - 1);
                  const AggClass = stops[0].color.constructor as new (c: any) => any;
                  const newStops = stops.map((s: any, i: number) => ({
                    color: i === safeIdx ? new AggClass(color) : s.color,
                    percent: s.percent,
                  }));
                  const newGradientColor = new AggClass(newStops);
                  setPickerValue(newGradientColor);
                  if (safeIdx === 0) setBrushColor(color);
                } else {
                  // Single-color mode: normal behavior.
                  setBrushColor(color);
                  setPickerValue(color);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
    );
  };
  // ── Menu items ───────────────────────────────────────────────────────────
  const fileMenuItems = useMemo(() => [
    { key: 'new',    label: menuLabel('New Project', `${CTRL}+${OPT}+N`),  onClick: () => checkAutosaveBeforeAction() },
    { key: 'open',   label: menuLabel('Open…', `${CTRL}+O`), onClick: () => viewportActions.open.current() },
    { key: 'save',   label: menuLabel('Save', `${CTRL}+S`),  onClick: () => viewportActions.save.current() },
    { key: 'saveas', label: menuLabel('Save As…'),    onClick: () => setProjectModalOpen(true) },
    { type: 'divider' as const },
    { key: 'render', label: menuLabel('Render…'),     onClick: () => setRenderModalOpen(true) },
  ], [setProjectModalOpen, setRenderModalOpen, checkAutosaveBeforeAction]);
  const editMenuItems = useMemo(() => [
    { key: 'undo', label: menuLabel('Undo', `${CTRL}+Z`), onClick: () => viewportActions.undo.current() },
    { key: 'redo', label: menuLabel('Redo', `${CTRL}+Y`), onClick: () => viewportActions.redo.current() },
    { type: 'divider' as const },
    { key: 'fill', label: menuLabel('Fill'),           onClick: () => viewportActions.fill.current() },
  ], []);
  const viewMenuItems = useMemo(() => [
    {
      key: 'background',
      label: 'Background',
      children: [
        { key: 'bg-black',       label: <>{chk(viewportBgMode === 'black')}Black</>,       onClick: () => setViewportBgMode('black') },
        { key: 'bg-white',       label: <>{chk(viewportBgMode === 'white')}White</>,       onClick: () => setViewportBgMode('white') },
        { key: 'bg-transparent', label: <>{chk(viewportBgMode === 'transparent')}Transparent</>, onClick: () => setViewportBgMode('transparent') },
      ],
    },
    {
      key: 'predefined cameras',
      label: 'Predefined Cameras',
      children: HERO_POSITIONS.map((h, i) => ({
        key: h.id,
        label: menuLabel(h.name, i < 8 ? String(i + 1) : undefined),
        onClick: () => viewportActions.snapToHero.current(h.id),
      })),
    },
    { type: 'divider' as const },
    { key: 'hero-snap',        label: <>{chk(heroFeaturesEnabled)}Hero Camera Snap</>, onClick: () => setHeroFeaturesEnabled(!heroFeaturesEnabled) },
    { key: 'show-grid',        label: <>{chk(showGrid)}Show Grid</>,        onClick: () => setShowGrid(!showGrid) },
    { key: 'show-manipulator', label: <>{chk(showManipulator)}Show Manipulator</>, onClick: () => setShowManipulator(!showManipulator) },
    { key: 'show-fps',         label: <>{chk(showFps)}Show FPS Counter</>,    onClick: () => setShowFps(!showFps) },
    { key: 'show-hotkey-hud',  label: <>{chk(showHotkeyHud)}Show Hotkey Reference</>, onClick: () => setShowHotkeyHud(!showHotkeyHud) },
    { type: 'divider' as const },
    { key: 'overlays', label: 'Overlays', children: [
      { key: 'overlay-architecture', label: menuLabel(<>{chk(overlayArchitecture)}Architecture</>, ','), onClick: () => setOverlayArchitecture(!overlayArchitecture) },
      { key: 'overlay-safety',       label: menuLabel(<>{chk(overlaySafety)}Safety</>, '.'),        onClick: () => setOverlaySafety(!overlaySafety) },
    ] },
    { type: 'divider' as const },
    { key: 'tablet-mode', label: <>{chk(tabletMode)}Tablet Mode</>, onClick: () => setTabletMode(!tabletMode) },
  ], [viewportBgMode, setViewportBgMode, heroFeaturesEnabled, setHeroFeaturesEnabled, showGrid, setShowGrid, showManipulator, setShowManipulator, showFps, setShowFps, showHotkeyHud, setShowHotkeyHud, overlayArchitecture, setOverlayArchitecture, overlaySafety, setOverlaySafety, tabletMode, setTabletMode]);
  const helpMenuItems = useMemo(() => [
    { key: 'guide',     label: 'User Guide',          onClick: () => setGuideOpen(true) },
    { key: 'hotkeys',   label: 'Keyboard Shortcuts',  onClick: () => setHotkeysOpen(true) },
    { key: 'whatsnew',  label: "What's New",          onClick: () => { setWhatsNewSince('0.0.0'); setWhatsNewOpen(true); } },
    { type: 'divider' as const },
    { key: 'about',     label: 'About StoryBall',     onClick: () => setAboutOpen(true) },
  ], [setHotkeysOpen, setAboutOpen, setWhatsNewOpen, setWhatsNewSince]);

  return (
    <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
      <AntdApp>
      {/* Lower-resolution confirmation */}
      <Modal
        open={pendingResTarget !== null}
        title="Lower resolution?"
        onOk={() => { if (pendingResTarget) setTexRes(pendingResTarget); setPendingResTarget(null); }}
        onCancel={() => setPendingResTarget(null)}
        okText="Lower"
        okButtonProps={{ danger: true }}
        width={360}
      >
        <p style={{ margin: 0 }}>Reducing resolution permanently discards detail — this cannot be undone.</p>
      </Modal>
      <Layout style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Content style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>

          {/* ── Menu bar ──────────────────────────────────────────────── */}
          <div style={{ ...rowStyle, height: 26, gap: 0, overflow: 'hidden' }}>
            <span style={{ color: C.textPrimary, fontWeight: 600, fontSize: 12, marginRight: 12, letterSpacing: '0.04em', fontFamily: 'monospace' }}>StoryBall</span>
            {[
              { label: 'File', items: fileMenuItems },
              { label: 'Edit', items: editMenuItems },
              { label: 'View', items: viewMenuItems },
              { label: 'Help', items: helpMenuItems },
            ].map(({ label, items }) => (
              <Dropdown key={label} menu={{ items }} trigger={['click']}>
                <span
                  style={{ color: C.textSecondary, fontSize: 12, padding: '3px 8px', cursor: 'pointer', borderRadius: 3, userSelect: 'none' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.selectBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {label}
                </span>
              </Dropdown>
            ))}
            <div style={{ flex: 1 }} />
            <span style={{ color: C.textDim, fontSize: 11 }}>{currentProjectName}</span>
          </div>

          {/* ── Main toolbar ──────────────────────────────────────────── */}
          <div className="tb-row" style={{ ...rowStyle, minHeight: 36 }}>

            {/* Tool mode buttons */}
            {([
              { mode: 'draw',  label: 'Draw',  tip: 'Draw (Q)' },
              { mode: 'fill',  label: 'Fill',  tip: 'Fill (W)' },
              { mode: 'erase', label: 'Erase', tip: 'Erase (E)' },
              { mode: 'stamp', label: 'Stamp', tip: 'Stamp (R)' },
              { mode: 'text',  label: 'Text',  tip: 'Text (T)' },
              { mode: 'blur',  label: 'Blur',  tip: 'Blur (Y)' },
            ] as const).map(({ mode, label, tip }) => (
              <Tooltip key={mode} title={tip} placement="bottom">
                <button
                  onClick={() => setToolMode(mode)}
                  style={toolBtn(toolMode === mode,
                    mode === 'erase' ? 'red'
                    : mode === 'fill' ? 'green'
                    : mode === 'stamp' ? 'yellow'
                    : mode === 'text'  ? 'purple'
                    : mode === 'blur'  ? 'cyan'
                    : 'blue')}
                >
                  {label}
                </button>
              </Tooltip>
            ))}

            <div style={{ flex: 1 }} />

            {/* Dup mode — centred */}
            {([
              { mode: 'hero',        label: 'Single',      tip: 'Single copy' },
              { mode: 'diptych',     label: 'Diptych',     tip: 'Diptych — mirror in two halves' },
              { mode: 'triptych',    label: 'Triptych',    tip: 'Triptych — three panels' },
              { mode: 'quadriptych', label: 'Quadriptych', tip: 'Quadriptych — four panels' },
            ] as const).map(({ mode, label, tip }) => (
              <Tooltip key={mode} title={tip} placement="bottom">
                <button onClick={() => setDupMode(mode)} style={toolBtn(dupMode === mode, 'blue')}>
                  {label}
                </button>
              </Tooltip>
            ))}

            <div style={{ flex: 1 }} />

            {/* Texture resolution */}
            {TEX_RESOLUTIONS.map((r) => (
              <Tooltip key={r.label} title={`${r.w} × ${r.h}`} placement="bottom">
                <button
                  onClick={() => {
                    if (r.w < texRes.w) {
                      setPendingResTarget(r);
                    } else {
                      setTexRes(r);
                    }
                  }}
                  style={toolBtn(texRes.label === r.label, 'blue')}
                >
                  {r.label}
                </button>
              </Tooltip>
            ))}
          </div>

          {/* ── Context strip: fill ───────────────────────────────── */}
          {toolMode === 'fill' && (
            <FillContextStrip colorPanelRender={colorPanelRender} handleBrushColorChange={handleBrushColorChange} />
          )}

          {/* ── Context strip: draw / erase ────────────────────────── */}
          {(toolMode === 'draw' || toolMode === 'erase') && (
            <DrawEraseContextStrip colorPanelRender={colorPanelRender} handleBrushColorChange={handleBrushColorChange} />
          )}

          {/* ── Context strip: blur ───────────────────────────────── */}
          {toolMode === 'blur' && (
            <BlurContextStrip />
          )}

          {/* ── Context strip: stamp / text ───────────────────────────── */}
          {(toolMode === 'stamp' || toolMode === 'text') && (
            <StampTextContextStrip colorPanelRender={colorPanelRender} handleBrushColorChange={handleBrushColorChange} />
          )}

          {/* ── Main area ────────────────────────────────────────────── */}
          {/*
            IMPORTANT: <Viewport3D /> must never move between React tree positions
            or it will remount and destroy its WebGL state. It always lives in
            SLOT A. CSS `order` is used to visually swap the two slots without
            any DOM unmounting.
          */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minWidth: 0, minHeight: 0, position: 'relative' }}>

            {/* ── Clipboard paste overlay ───────────────────────────── */}
            {clipboardPasteCanvas && (
              <div style={{
                position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                zIndex: 50, display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(10, 18, 38, 0.88)', backdropFilter: 'blur(8px)',
                border: '1px solid #558cf4', borderRadius: 8,
                padding: '5px 10px', boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                pointerEvents: 'all',
              }}>
                {pastePreviewUrl && (
                  <img src={pastePreviewUrl} alt="paste" style={{ height: 28, maxWidth: 48, objectFit: 'contain', borderRadius: 2, border: '1px solid #2a2a4a', flexShrink: 0 }} />
                )}
                <span style={{ fontSize: 11, color: '#aaa', flexShrink: 0, whiteSpace: 'nowrap' }}>Paste as Layer</span>
                <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Scale</span>
                <Tooltip title={`${Math.round(clipboardPasteScale * 100)}% of fit`} placement="top">
                  <input
                    type="range" min={0.1} max={3.0} step={0.05} value={clipboardPasteScale}
                    onChange={(e) => setClipboardPasteScale(Number(e.target.value))}
                    style={{ width: 100, cursor: 'pointer', accentColor: '#558cf4' }}
                  />
                </Tooltip>
                <span style={{ fontSize: 11, color: '#aaa', fontVariantNumeric: 'tabular-nums', minWidth: 34, flexShrink: 0 }}>
                  {Math.round(clipboardPasteScale * 100)}%
                </span>
                <Tooltip title="Create a new layer containing the pasted image" placement="top">
                  <button onClick={() => viewportActions.commitClipboardPaste.current()} style={actionBtn('blue')}>
                    Create Layer
                  </button>
                </Tooltip>
                <Tooltip title="Discard paste" placement="top">
                  <button onClick={() => viewportActions.cancelClipboardPaste.current()} style={actionBtn('red')}>
                    Cancel
                  </button>
                </Tooltip>
              </div>
            )}

            {/* ── SLOT A: Viewport3D — fixed DOM position, never moves ── */}
            <div style={{
              order: uvViewportMain ? 2 : 0,
              flex: (uvViewportMain && rightPanelOpen) ? `0 0 ${rightWidth}px` : '1 1 0',
              width: (uvViewportMain && rightPanelOpen) ? rightWidth : undefined,
              minWidth: 0, minHeight: 0,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden', position: 'relative',
            }}>
              {/*
                Viewport3D is ALWAYS at: SLOT-A > outer-wrap > inner-wrap > <Viewport3D />
                Only the styles on these two wrapper divs change — never their tree position.
                Changing depth would cause React to unmount/remount Viewport3D, destroying
                all WebGL state (layers, camera, render targets).
              */}
              {/* Outer wrapper: panel-mode adds padding; main-mode is a flex fill */}
              <div style={uvViewportMain && rightPanelOpen
                ? { padding: 10, flexShrink: 0 }
                : { flex: '1 1 0', position: 'relative', overflow: 'hidden' }
              }>
                {/* Inner wrapper: panel-mode constrains to 2:1; main-mode fills parent */}
                <div style={uvViewportMain && rightPanelOpen
                  ? { width: '100%', aspectRatio: '2 / 1', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }
                  : { position: 'absolute', inset: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                }>
                  <Viewport3D />
                  <HotkeyHud />
                  {/* Blocker + swap button — only in panel mode */}
                  {(uvViewportMain && rightPanelOpen) && (
                    <>
                      {/* Absorbs ALL pointer events so the 3D viewport cannot be interacted with */}
                      <div style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'default' }} />
                      <button
                        onClick={() => setUvViewportMain(false)}
                        title="Switch back to 3D viewport (drawing on sphere)"
                        style={{
                          position: 'absolute', bottom: 4, left: 4,
                          padding: '2px 5px', fontSize: 10, lineHeight: '16px',
                          borderRadius: 3, border: '1px solid #558cf4', cursor: 'pointer',
                          background: '#2a2a4a', color: '#ddd',
                          userSelect: 'none', zIndex: 10,
                        }}
                      >
                        <FullscreenOutlined />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* LayersPanel sits below 3D when it is in the panel slot */}
              {(uvViewportMain && rightPanelOpen) && (
                <div style={{ flex: 1, overflowY: 'auto', borderTop: `1px solid ${C.border}` }}>
                  <LayersPanel />
                </div>
              )}
              {/* Toggle buttons — only when 3D is the visually-main slot */}
              {!uvViewportMain && (
                <>
                  <button
                    onClick={() => setRightPanelOpen(!rightPanelOpen)}
                    title={rightPanelOpen ? 'Hide panels' : 'Show panels'}
                    style={{
                      position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                      width: 24, height: 48, borderRadius: 6, border: 'none', cursor: 'pointer',
                      background: C.bgFloat, color: C.textSecondary, fontSize: 14,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 10, backdropFilter: 'blur(4px)',
                    }}
                  >
                    {rightPanelOpen ? '▸' : '◂'}
                  </button>
                  <button
                    onClick={() => setTimelineOpen(!timelineOpen)}
                    title={timelineOpen ? 'Hide timeline' : 'Show timeline'}
                    style={{
                      position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)',
                      width: 48, height: 24, borderRadius: 6, border: 'none', cursor: 'pointer',
                      background: C.bgFloat, color: C.textSecondary, fontSize: 14,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 10, backdropFilter: 'blur(4px)',
                    }}
                  >
                    {timelineOpen ? '▾' : '▴'}
                  </button>
                </>
              )}
              {/* (tablet controls are rendered inside Viewport3D) */}
            </div>

            {/* Divider */}
            {rightPanelOpen && (
              <div style={{ order: 1, width: 1, background: C.border, alignSelf: 'stretch', flexShrink: 0 }} />
            )}

            {/* ── SLOT B: UVPanel — fixed DOM position, never moves ── */}
            {rightPanelOpen && (
              <div style={{
                order: uvViewportMain ? 0 : 2,
                flex: uvViewportMain ? '1 1 0' : `0 0 ${rightWidth}px`,
                width: uvViewportMain ? undefined : rightWidth,
                minWidth: uvViewportMain ? 0 : rightWidth,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden', position: 'relative',
                pointerEvents: (isDrawingActive && !uvViewportMain) ? 'none' : undefined,
              }}>
                {uvViewportMain ? (
                  // UV is visually main: centred 2:1 + toggle buttons as overlay
                  <>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#000', minHeight: 0 }}>
                      <div style={{ width: '100%', aspectRatio: '2 / 1', maxHeight: '100%' }}>
                        <UVPanel />
                      </div>
                    </div>
                    <button
                      onClick={() => setRightPanelOpen(!rightPanelOpen)}
                      title={rightPanelOpen ? 'Hide panels' : 'Show panels'}
                      style={{
                        position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                        width: 24, height: 48, borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: C.bgFloat, color: C.textSecondary, fontSize: 14,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 10, backdropFilter: 'blur(4px)',
                      }}
                    >
                      {rightPanelOpen ? '▸' : '◂'}
                    </button>
                    <button
                      onClick={() => setTimelineOpen(!timelineOpen)}
                      title={timelineOpen ? 'Hide timeline' : 'Show timeline'}
                      style={{
                        position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)',
                        width: 48, height: 24, borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: C.bgFloat, color: C.textSecondary, fontSize: 14,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 10, backdropFilter: 'blur(4px)',
                      }}
                    >
                      {timelineOpen ? '▾' : '▴'}
                    </button>
                  </>
                ) : (
                  // Normal panel: UVPanel (2:1) + LayersPanel
                  <>
                    <div style={{ padding: 10, flexShrink: 0 }}>
                      <div style={{ width: '100%', aspectRatio: '2 / 1', maxWidth: '100%' }}>
                        <UVPanel />
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', borderTop: `1px solid ${C.border}` }}>
                      <LayersPanel />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {timelineOpen && (
            <>
              <div
                onMouseDown={onTimelineResizeMouseDown}
                style={{ height: 6, cursor: 'ns-resize', flexShrink: 0, borderTop: `1px solid ${C.border}`, background: 'transparent' }}
              />
              <div style={{ height: timelineHeight, flexShrink: 0, pointerEvents: isDrawingActive ? 'none' : undefined }}>
                <Timeline />
              </div>
            </>
          )}
        </Content>
        <Footer style={{ textAlign: 'center', padding: '2px 8px', background: C.bgDeep, color: C.textFaint, borderTop: `1px solid ${C.border}`, flexShrink: 0, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          StoryBall v{pkg.version} &nbsp;·&nbsp; © 2026 10lbHam
          <span
            title={autosaveStatus === 'clean' ? 'All changes saved' : autosaveStatus === 'saving' ? 'Autosaving…' : 'Unsaved changes'}
            style={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              background: autosaveStatus === 'clean' ? C.textFaint : autosaveStatus === 'saving' ? C.toolFill : C.toolStamp,
              transition: 'background 0.4s',
            }}
          />
        </Footer>
      </Layout>

      {/* ── Modals lifted to top level ──────────────────────────────── */}
      <HotkeyModal open={hotkeysOpen} onClose={() => setHotkeysOpen(false)} />
      <StampPickerModal open={stampPickerOpen} onClose={() => setStampPickerOpen(false)} />
      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <UserGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
      <WhatsNewModal open={whatsNewOpen} sinceVersion={whatsNewSince} onClose={() => setWhatsNewOpen(false)} />
      <Modal
        open={recoverySlot !== null}
        title="Restore unsaved work?"
        onOk={() => recoverySlot && handleRestore(recoverySlot.json)}
        onCancel={handleDiscard}
        okText="Restore"
        cancelText={pendingFileData ? 'Load from disk' : 'Discard'}
        width={400}
      >
        {recoverySlot && (
          <p style={{ margin: 0 }}>
            Autosaved changes were found for <strong>"{recoverySlot.name}"</strong>.
            {pendingFileData
              ? ' Restore the autosave to pick up where you left off, or load the file from disk to discard them.'
              : ' Restore to pick up where you left off, or discard to start fresh.'}
          </p>
        )}
      </Modal>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;

